import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { leagueAdminRepository } from "@/config/dataSource";
import { leaguesQueryKeys } from "@/modules/leagues/hooks/useLeagues";
import type { LeagueRole } from "@/modules/leagues/types/league";
import type { GeneralSettingsInput } from "@/modules/leagues/types/admin";

const adminKeys = {
  clubs: (competitionId: string) => ["clubs", competitionId] as const,
  history: (leagueId: string) => ["league-history", leagueId] as const,
  adjustments: (leagueId: string) => ["manual-adjustments", leagueId] as const,
  participants: (leagueId: string) => ["participants", leagueId] as const,
  bonusQuestions: (leagueId: string) => ["bonus-questions", leagueId] as const,
};

export function useClubs(competitionId: string | undefined) {
  return useQuery({
    queryKey: adminKeys.clubs(competitionId ?? "unknown"),
    queryFn: () => leagueAdminRepository.getClubs(competitionId as string),
    enabled: Boolean(competitionId),
  });
}

export function useUpdateDisabledClubs(leagueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (disabledClubIds: string[]) => leagueAdminRepository.updateDisabledClubs(leagueId, disabledClubIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leaguesQueryKeys.detail(leagueId) });
      queryClient.invalidateQueries({ queryKey: adminKeys.history(leagueId) });
    },
  });
}

export function useUpdateGeneralSettings(leagueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: GeneralSettingsInput) => leagueAdminRepository.updateGeneralSettings(leagueId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leaguesQueryKeys.detail(leagueId) });
      queryClient.invalidateQueries({ queryKey: leaguesQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: adminKeys.history(leagueId) });
    },
  });
}

export function useLeagueHistory(leagueId: string) {
  return useQuery({
    queryKey: adminKeys.history(leagueId),
    queryFn: () => leagueAdminRepository.getHistory(leagueId),
  });
}

export function useManualAdjustments(leagueId: string) {
  return useQuery({
    queryKey: adminKeys.adjustments(leagueId),
    queryFn: () => leagueAdminRepository.getManualAdjustments(leagueId),
  });
}

export function useAddManualAdjustment(leagueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ participantUid, pontos, motivo }: { participantUid: string; pontos: number; motivo: string }) =>
      leagueAdminRepository.addManualAdjustment(leagueId, participantUid, pontos, motivo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.adjustments(leagueId) });
      queryClient.invalidateQueries({ queryKey: adminKeys.history(leagueId) });
      queryClient.invalidateQueries({ queryKey: adminKeys.participants(leagueId) });
    },
  });
}

export function useLeagueParticipants(leagueId: string) {
  return useQuery({
    queryKey: adminKeys.participants(leagueId),
    queryFn: () => leagueAdminRepository.getParticipants(leagueId),
  });
}

export function useUpdateParticipantRole(leagueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uid, role }: { uid: string; role: LeagueRole }) =>
      leagueAdminRepository.updateParticipantRole(leagueId, uid, role),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminKeys.participants(leagueId) }),
  });
}

export function useRemoveParticipant(leagueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uid: string) => leagueAdminRepository.removeParticipant(leagueId, uid),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminKeys.participants(leagueId) }),
  });
}

export function useBonusQuestions(leagueId: string) {
  return useQuery({
    queryKey: adminKeys.bonusQuestions(leagueId),
    queryFn: () => leagueAdminRepository.getBonusQuestions(leagueId),
  });
}

export function useCreateBonusQuestion(leagueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { pergunta: string; opcoes: string[]; pontos: number; rodada: number }) =>
      leagueAdminRepository.createBonusQuestion(leagueId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.bonusQuestions(leagueId) });
      queryClient.invalidateQueries({ queryKey: adminKeys.history(leagueId) });
    },
  });
}
